// Entry point.
//
// ONE bundle, many instances. A single page can carry any mix of:
//
//   1. Inline booking sections — every element with `data-cc-inline-widget`
//      mounts its OWN independent Widget (own React root) that renders Step 1
//      in-flow. Each reads its OWN phone flavour (`data-cc-phone`) and
//      attribution (`data-cc-embed-source`) off that element, so two inline
//      sections with different flavours can coexist.
//
//   2. A site-wide modal — any element with `data-cc-booking-widget` or
//      `data-cc-book-now` is wired as a click trigger. A SINGLE modal Widget
//      instance (hidden, appended to <body>) serves ALL triggers. Each
//      trigger, on click, dispatches the engage event carrying THAT trigger's
//      own phone flavour + embed source, so a nav "BOOK NOW" (phone optional)
//      and a promo button (phone mandatory) can share one modal instance and
//      each open Step 1 in its own flavour.
//
//   3. Coexistence — inline sections and the modal are ALWAYS both wired,
//      independent of each other. A page can have an inline hero section AND
//      the nav BOOK NOW modal at the same time; each runs its own state
//      machine and neither responds to the other's engage.
//
//   4. If a `data-cc-booking-widget` element is empty, a default
//      "Book a Fitting" button is rendered inside it.
//
// Embed contract attributes: `data-cc-inline-widget`, `data-cc-book-now` /
// `data-cc-booking-widget`, `data-cc-phone`, `data-cc-embed-source`,
// `data-cc-fitter-assignment`.
// See ../../clubchampion-docs/architecture/widget-embedding-contract.md

import { useEffect, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { Widget } from './widget';
import { ENGAGE_EVENT } from './state/events';
import { loadPromoWindows, promoWindowsUrl } from './data/promoWindows';
import {
  DEFAULT_FITTER_ASSIGNMENT,
  FitterAssignmentContext,
  readFitterAssignment,
  type FitterAssignment,
} from './utils/fitterAssignment';
import { resolveCountryConfig, type CountryConfig } from './config/countries';
import { getAttribution } from './analytics/attribution';
import { fireWidgetPageView } from './analytics/synthetic-pageview';
import { initWafToken } from './api/waf-token';
import { dispatchExposure, getGrowthBookVisitorId } from './analytics/dispatch';
import {
  PHONE_FIELD_POSITION_CONTROL,
  PHONE_FIELD_POSITION_FEATURE,
  PHONE_FIELD_POSITION_TREATMENT,
  type PhoneFieldPosition,
} from './experiments/phoneFieldPosition';

// Build metadata — substituted by vite.config.ts's define block at compile time
// from VITE_BUILD_SHA / VITE_BUILD_TIME env vars exported by scripts/aws-deploy.sh.
// The same bundle deploys to both prod and staging, so env is intentionally not
// stamped here; it's set per-env in the host.html substitution instead. Exposed
// at runtime so anyone can answer "what commit is on prod vs staging?" by
// grepping either bundle for CC_WIDGET_BUILD or by opening devtools.
// The bundle's own URL, read while this script is still the one executing —
// document.currentScript is null once execution has moved on. It locates the
// promo-windows file that lives beside the bundle.
const SCRIPT_SRC: string | null =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript.src || null
    : null;

declare const __BUILD_SHA__: string;
declare const __BUILD_TIME__: string;

const BUILD_INFO = {
  sha: __BUILD_SHA__,
  time: __BUILD_TIME__,
} as const;

(window as unknown as { CC_WIDGET_BUILD: typeof BUILD_INFO }).CC_WIDGET_BUILD = BUILD_INFO;
// One-line boot marker — visible in devtools, easy to spot in support tickets.
console.info(`[CC widget] ${BUILD_INFO.sha} ${BUILD_INFO.time}`);

const TRIGGER_SELECTORS = ['[data-cc-booking-widget]', '[data-cc-book-now]'];
const DEFAULT_TRIGGER_TEXT = 'Book a Fitting';

// GrowthBook proxy provisioned alongside the sGTM container (same ECS host
// pattern). Not the public cdn.growthbook.io — traffic goes through CC's own
// endpoint so an ad blocker's third-party list doesn't null out experiment
// assignment for the exact cohort we most want to measure.
const GROWTHBOOK_API_HOST = 'https://cl-e40a932bd6434b93bb2e987f4377904d.ecs.us-east-1.on.aws';

// Cap on how long a visible inline mount will wait for experiment config
// before falling back to control. Only ever paid once GrowthBook is actually
// configured — with no client key, initGrowthBook() returns synchronously.
const GROWTHBOOK_INIT_TIMEOUT_MS = 1000;

// Host CC_WIDGET_CONFIG wins; otherwise the build-time baked key. Without the
// fallback, experiments only run on embeds whose host page sets the key — which
// excludes the prod Joomla page, where config is hand-maintained across several
// modules. The key is a GrowthBook *client* key: read-only, public by design,
// and already served in cleartext by every host page, so baking it changes
// nothing about exposure.
const GROWTHBOOK_CLIENT_KEY =
  window.CC_WIDGET_CONFIG?.growthbookClientKey ??
  import.meta.env.VITE_CC_DEFAULT_GROWTHBOOK_KEY ??
  '';

// GrowthBook — a single instance created once at widget boot (module scope,
// not inside a mount function, so every instance on the page shares one set of
// assignments and one exposure fire).
const growthbook = new GrowthBook({
  apiHost: GROWTHBOOK_API_HOST,
  clientKey: GROWTHBOOK_CLIENT_KEY,
  enableDevMode: import.meta.env.DEV,
  // Assignments are read once per mount (inline) or once per click (modal) and
  // then threaded through the state machine as a prop. Live-updating them
  // mid-flow would move the phone field under a user who is already typing,
  // so we deliberately do NOT subscribe to changes.
  subscribeToChanges: false,
  attributes: {
    // Stable cross-visit browser id — NOT the session-scoped web_submission_id.
    // See getGrowthBookVisitorId() for why the distinction matters.
    id: getGrowthBookVisitorId(),
    device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
  },
  trackingCallback: (experiment, result) => {
    dispatchExposure(experiment.key, result.variationId, result.value);
  },
});

// No client key from EITHER source => GrowthBook is not provisioned for this
// embed. Every experiment resolves to its control default and no network call
// is made.
//
// This must test the resolved key, not the host config. Testing the host config
// here while the SDK above reads the resolved value would leave a correctly
// configured SDK permanently disabled on any page without host config — an
// experiment that silently never runs, with nothing in the bundle to show why.
const growthbookEnabled = Boolean(GROWTHBOOK_CLIENT_KEY);

async function initGrowthBook(): Promise<void> {
  if (!growthbookEnabled) return;
  try {
    await growthbook.init({ timeout: GROWTHBOOK_INIT_TIMEOUT_MS, streaming: false });
  } catch {
    // An experiment service failure must never block the booking widget.
    // The SDK keeps serving defaults, i.e. control.
  }
}

// phone-field-position experiment. Control is byte-for-byte today's behavior.
function resolvePhoneFieldPosition(): PhoneFieldPosition {
  if (!growthbookEnabled) return PHONE_FIELD_POSITION_CONTROL;
  // The value is authored in the GrowthBook UI, so it's remote input: anything
  // we don't recognise (typo, a variant added without a widget release) falls
  // back to control rather than being trusted into the flow.
  const value = growthbook.getFeatureValue<string>(
    PHONE_FIELD_POSITION_FEATURE,
    PHONE_FIELD_POSITION_CONTROL,
  );
  return value === PHONE_FIELD_POSITION_TREATMENT
    ? PHONE_FIELD_POSITION_TREATMENT
    : PHONE_FIELD_POSITION_CONTROL;
}

// Phone "flavour", read off the SAME container/trigger div that already
// dictates modal-vs-inline. `data-cc-phone="mandatory"` collects the phone on
// Step 1 (required); anything else keeps the production behavior: phone is
// optional and collected on the last (Get Set Up) step.
//
// An explicit `data-cc-phone` on the element always wins — a host page that
// has committed to a flavour must not be silently re-bucketed by an
// experiment. Only when the attribute is ABSENT does the phone-field-position
// experiment get to choose, so turning the experiment on can never contradict
// a page that asked for a specific form.
function readPhoneMandatory(el: Element | null): boolean {
  const v = el?.getAttribute('data-cc-phone')?.trim().toLowerCase();
  if (v) return v === 'mandatory' || v === 'required' || v === 'first';

  // The experiment never answers for an international market. It is a clean
  // US-only test — international traffic entering the buckets would move the
  // result without telling us why — and since 2026-10-10 those markets
  // collect the phone on the Confirm step instead (CountryConfig
  // .phoneOnConfirmStep), so putting it on Step 1 as well would ask twice.
  const country = readCountryConfig(el);
  if (country?.phoneOnFirstStep) return true;
  if (country && country.code !== 'us') return false;

  return resolvePhoneFieldPosition() === PHONE_FIELD_POSITION_TREATMENT;
}

// Attribution "embed source" for a given mount element. Mirrors
// readPhoneMandatory: read `data-cc-embed-source` off the element first, then
// fall back to the page-wide CC_WIDGET_CONFIG.embedSource, then a mode-specific
// default ('inline' for inline containers, 'unknown' for the modal). This lets
// two sections on one page attribute independently.
function readEmbedSource(el: Element | null, fallback: string): string {
  const v = el?.getAttribute('data-cc-embed-source')?.trim();
  if (v) return v;
  return window.CC_WIDGET_CONFIG?.embedSource ?? fallback;
}

// Country config for a given mount element. Reads `data-cc-countrycode` and
// resolves to a CountryConfig (see src/config/countries.ts). Returns null when
// the attribute is absent or unrecognized — caller keeps the page-wide default
// behavior. Two triggers on one page can differ (a UK-specific promo button
// and a US-default hero, for example).
function readCountryConfig(el: Element | null): CountryConfig | null {
  return resolveCountryConfig(el?.getAttribute('data-cc-countrycode'));
}

// Promo override for a given mount element. `data-cc-promoverride="FOO"`
// forces `FOO` as the applied promo code regardless of the country default.
// Precedence: promoverride > country.initialPromoCode > no auto-apply. Useful
// for campaign-specific pages that need a one-off promo distinct from the
// country's standard starter.
//
// Fallback: `?cc_promoverride=FOO` on the host page URL, so a campaign code
// can be tried on any preview page without editing its markup (Jon,
// 2026-10-12). The attribute still wins when both are present.
function readPromoOverride(el: Element | null): string | null {
  const v = el?.getAttribute('data-cc-promoverride')?.trim();
  if (v && v.length > 0) return v.toUpperCase();
  try {
    for (const [k, val] of new URLSearchParams(window.location.search)) {
      if (k.toLowerCase() === 'cc_promoverride' && val.trim()) return val.trim().toUpperCase();
    }
  } catch {
    /* no window / malformed query — attribute-only behaviour */
  }
  return null;
}

// Inline landing-page mode. A host page that wants the widget embedded in-page
// (Step 1 card that promotes to the full-screen modal at Step 2) drops a
// container:  <div data-cc-inline-widget></div>. EVERY such element mounts its
// own independent inline Widget; this no longer suppresses the modal path — the
// modal is always wired too so both can coexist on the same page.
const INLINE_SELECTOR = '[data-cc-inline-widget]';

function fireEngage(el: Element, source: string): void {
  // Reuse the existing ENGAGE_EVENT contract so analytics + scroll
  // behavior already wired into the widget continues to work. Raw
  // window.dispatchEvent (not the typed emit() helper) because the
  // engage event isn't in WidgetEventDetails — it's a host→widget
  // signal, not a widget→host one.
  //
  // The detail carries THIS trigger's own phone flavour + embed source so the
  // single shared modal instance can configure Step 1 for this specific open
  // (a nav button and a promo button can differ, one modal instance).
  const country = readCountryConfig(el);
  const promoOverride = readPromoOverride(el);
  window.dispatchEvent(
    new CustomEvent(ENGAGE_EVENT, {
      detail: {
        source,
        phoneMandatory: readPhoneMandatory(el),
        embedSource: readEmbedSource(el, 'unknown'),
        // Who takes an Any Fitter time for THIS open — data-cc-fitter-assignment
        // on the clicked trigger (utils/fitterAssignment.ts).
        fitterAssignment: readFitterAssignment(el),
        // Country-specific overrides — resolved from data-cc-countrycode. Null
        // if the trigger has no attribute; widget falls back to default behavior.
        country,
        // Absolute promo override — data-cc-promoverride wins over the country
        // default. Set for campaign pages that need a one-off code. Null when
        // the attribute is absent.
        promoOverride,
        // Location-context: a trigger (e.g. a fitter's "Book fitting" CTA) can
        // force the studio and preselect a fitter for this open. Null/absent on
        // ordinary BOOK NOW triggers, which behave exactly as before.
        presetStoreName: el.getAttribute('data-cc-store')?.trim() || null,
        presetFitterName: el.getAttribute('data-cc-fitter')?.trim() || null,
      },
    }),
  );
  if (country) {
    // eslint-disable-next-line no-console
    console.info(`[CC widget] country=${country.locale} promo=${country.initialPromoCode}`);
  }
  if (promoOverride) {
    // eslint-disable-next-line no-console
    console.info(`[CC widget] promoOverride=${promoOverride}`);
  }
  // Treat widget invocation as a page_view: it's a meaningful
  // page-equivalent interaction and, more importantly, it gives GA4 a
  // session-anchoring event with proper dl + dr + attribution for the
  // ad-blocked / ITP-affected cohort whose host page_view is suppressed.
  // Idempotent — fires at most once per script load even if the user
  // engages multiple triggers.
  fireWidgetPageView();
}

function wireTriggers(): void {
  const seen = new WeakSet<Element>();
  for (const selector of TRIGGER_SELECTORS) {
    for (const el of Array.from(document.querySelectorAll(selector))) {
      if (seen.has(el)) continue;
      seen.add(el);

      const source = el.getAttribute('data-cc-trigger-source') ?? el.id ?? selector;

      // For empty [data-cc-booking-widget] divs (the legacy placeholder),
      // render a default trigger button inside so the host page has
      // something visible to click. Pages that already supply their own
      // button (e.g. hero CTA with custom copy) are left alone.
      if (
        selector === '[data-cc-booking-widget]' &&
        el instanceof HTMLElement &&
        el.childNodes.length === 0
      ) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cc-default-trigger';
        btn.textContent = DEFAULT_TRIGGER_TEXT;
        // Inline minimal styling so the button looks reasonable even on
        // hosts that haven't loaded the widget's CSS module yet. Hosts
        // can override by styling [data-cc-booking-widget] > .cc-default-trigger.
        btn.style.cssText = [
          'background:#193b3f',
          'color:#ffffff',
          'border:none',
          'border-radius:999px',
          'padding:12px 24px',
          'font-size:15px',
          'font-weight:600',
          'cursor:pointer',
          'font-family:inherit',
        ].join(';');
        el.appendChild(btn);
      }

      el.addEventListener('click', (e) => {
        // Don't hijack clicks on internal anchors / form controls inside
        // the trigger; only the trigger element itself opens the modal.
        const target = e.target as Element | null;
        if (target && target !== el && target.tagName === 'A' && target.getAttribute('href')) {
          return;
        }
        e.preventDefault();
        fireEngage(el, source);
      });
    }
  }
}

// The shared modal takes its fitter-assignment rule per OPEN, from the trigger
// that was clicked, exactly as it takes its phone flavour. An inline mount is
// simpler — the rule is fixed by its container — so it provides the context
// directly in mountInlineWidget.
function ModalFitterAssignment({ children }: { children: ReactNode }) {
  const [assignment, setAssignment] = useState<FitterAssignment>(DEFAULT_FITTER_ASSIGNMENT);
  useEffect(() => {
    const onEngage = (e: Event) => {
      const detail = (e as CustomEvent).detail as { fitterAssignment?: FitterAssignment } | undefined;
      setAssignment(detail?.fitterAssignment ?? DEFAULT_FITTER_ASSIGNMENT);
    };
    window.addEventListener(ENGAGE_EVENT, onEngage);
    return () => window.removeEventListener(ENGAGE_EVENT, onEngage);
  }, []);
  return <FitterAssignmentContext.Provider value={assignment}>{children}</FitterAssignmentContext.Provider>;
}

// The SINGLE modal instance. One hidden Widget serves every trigger on the
// page; per-open phone flavour + embed source ride on the engage event detail
// (see fireEngage), so this instance's props are only defaults. Mount it even
// when no triggers exist yet, so late-added triggers (MutationObserver) have a
// modal to open.
function mountModalWidget(defaultPhoneMandatory: boolean): void {
  const embedSource = readEmbedSource(null, 'unknown');

  // Single mount container appended to body — the modal portals render
  // on top of the page regardless of where this lives.
  const host = document.createElement('div');
  host.setAttribute('data-cc-widget-host', '');
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(
    <GrowthBookProvider growthbook={growthbook}>
      <ModalFitterAssignment>
        <Widget embedSource={embedSource} phoneMandatory={defaultPhoneMandatory} />
      </ModalFitterAssignment>
    </GrowthBookProvider>,
  );
}

function mountInlineWidget(container: Element): void {
  const embedSource = readEmbedSource(container, 'inline');
  // The attribute VALUE selects the inline presentation. Bare
  // `data-cc-inline-widget` keeps the classic landing-page card, unchanged;
  // `data-cc-inline-widget="card"` renders the compact seed card (the
  // hero-embedded "START YOUR BOOKING" panel), which may also carry:
  //   data-cc-fitting  canonical fitting name to preselect ("Full Bag")
  //   data-cc-heading  the card's title ("Book a full bag fitting near you")
  const variantAttr = container.getAttribute('data-cc-inline-widget')?.trim().toLowerCase();
  const inlineVariant = (variantAttr ?? '').startsWith('card') ? 'card' as const : 'classic' as const;
  // `data-cc-inline-widget="card wide"` opts the seed card into the two-column
  // layout. A modifier on the existing attribute rather than a new one, because
  // it is meaningless without variant=card and a separate attribute invites the
  // combination that has no meaning.
  const cardWide = (variantAttr ?? '').includes('wide');
  // `card basic` drops the fitting-type row: Name, Email and Studio only. For a
  // mount with no fitting context — the home hero — where asking which fitting
  // before the visitor has picked a studio is a question too early.
  const cardBasic = (variantAttr ?? '').includes('basic');
  const presetFittingName = container.getAttribute('data-cc-fitting')?.trim() || undefined;
  const cardHeading = container.getAttribute('data-cc-heading')?.trim() || undefined;
  // Location-context override (store detail page): `data-cc-store` forces the
  // studio (winning over the favourite-store hint), and optional `data-cc-fitter`
  // preselects a fitter at that studio.
  const presetStoreName = container.getAttribute('data-cc-store')?.trim() || undefined;
  const presetFitterName = container.getAttribute('data-cc-fitter')?.trim() || undefined;
  // data-cc-mode="reschedule" boots the inline instance straight into the
  // reschedule flow, prefilled from data-cc-prefill-name/-email (both
  // required for the auto-lookup) and optionally narrowed to
  // data-cc-appointment (an A-number) so a page that already knows which
  // appointment is moving skips the picker. The manage-appointment page's
  // contract.
  let rescheduleBoot = null;
  if (container.getAttribute('data-cc-mode') === 'reschedule') {
    const name = (container.getAttribute('data-cc-prefill-name') ?? '').trim();
    const email = (container.getAttribute('data-cc-prefill-email') ?? '').trim();
    if (name && email) {
      rescheduleBoot = {
        name,
        email,
        appointment: (container.getAttribute('data-cc-appointment') ?? '').trim() || undefined,
      };
    }
  }
  // Market + promo override, read off the container exactly as fireEngage
  // reads them off a trigger. countries.ts has always documented that an
  // inline container may carry data-cc-countrycode; until 2026-10-17 the
  // inline path never read it, so every inline mount was the US flow.
  const country = readCountryConfig(container);
  const promoOverride = readPromoOverride(container);
  if (country) {
    // eslint-disable-next-line no-console
    console.info(`[CC widget] inline country=${country.locale} promo=${country.initialPromoCode}`);
  }
  if (promoOverride) {
    // eslint-disable-next-line no-console
    console.info(`[CC widget] inline promoOverride=${promoOverride}`);
  }
  const root = createRoot(container);
  root.render(
    <GrowthBookProvider growthbook={growthbook}>
      <FitterAssignmentContext.Provider value={readFitterAssignment(container)}>
        <Widget
          embedSource={embedSource}
          inline
          country={country}
          promoOverride={promoOverride}
          inlineVariant={inlineVariant}
          cardWide={cardWide}
          cardBasic={cardBasic}
          presetFittingName={presetFittingName}
          cardHeading={cardHeading}
          presetStoreName={presetStoreName}
          presetFitterName={presetFitterName}
          phoneMandatory={readPhoneMandatory(container)}
          rescheduleBoot={rescheduleBoot}
        />
      </FitterAssignmentContext.Provider>
    </GrowthBookProvider>,
  );
  // The inline widget shows the booking flow on load (no engage click), so
  // fire the widget page_view directly for GA4 session-anchoring parity with
  // the button-triggered embed. Idempotent — at most once per script load.
  fireWidgetPageView();
}

function init(): void {
  // Persist UTM / gclid attribution from the landing URL into sessionStorage
  // immediately, before any host-page navigation can strip the query string.
  // Joomla pages are full reloads, so URL params from the original ad click
  // only exist on the landing page; without capturing them now, a user who
  // navigates to /locations and then opens the modal has no way to recover
  // their original campaign attribution. getAttribution() reads the URL +
  // _gcl_aw cookie and writes the result to sessionStorage; subsequent page
  // loads on the same tab merge URL (empty) with storage (preserved) and
  // keep the original landing attribution intact.
  getAttribution();
  // The promo fitting windows, from the file beside the bundle. One fetch,
  // never awaited, never fatal — see data/promoWindows.ts.
  void loadPromoWindows(promoWindowsUrl(SCRIPT_SRC));
  // Kick off the WAF SDK load early so the token is available by the time
  // the user finishes the booking flow and hits the protected endpoints.
  // No-op when CC_WIDGET_CONFIG.wafIntegrationUrl is unset.
  initWafToken();

  // 1) ALWAYS mount the single modal instance + wire triggers FIRST, whether or
  //    not inline sections are present. This is what lets a site-wide nav
  //    "BOOK NOW" modal coexist on the same page as an inline hero section, and
  //    lets this one bundle supplant the legacy site-wide widget.
  //
  //    This runs synchronously, BEFORE the GrowthBook fetch below, so a slow or
  //    blocked experiment service can never open a window where a BOOK NOW
  //    click lands on an unwired trigger and does nothing.
  //
  //    The modal's props are only DEFAULTS — each trigger click carries its own
  //    flavour + embed source in the engage detail, resolved at click time (by
  //    which point experiment config has almost always landed). Seed the
  //    default flavour off the first trigger that declares `data-cc-phone` so a
  //    page with a single trigger behaves exactly as before even before the
  //    first click.
  const firstFlavouredTrigger = document.querySelector(
    TRIGGER_SELECTORS.map((s) => `${s}[data-cc-phone]`).join(', '),
  );
  mountModalWidget(readPhoneMandatory(firstFlavouredTrigger));
  wireTriggers();

  // 2) Mount EVERY inline container as its own independent Widget instance.
  //    Each reads its own flavour + embed source off its element, so multiple
  //    inline sections with different flavours coexist. Inline instances are
  //    always-on (render Step 1 immediately) and do NOT react to engage.
  //
  //    Unlike the hidden modal, an inline section paints Step 1 straight away,
  //    so its flavour has to be settled BEFORE first paint — otherwise the
  //    phone field would pop into an already-visible form. Hence the await.
  //    With no client key configured this resolves synchronously and mounting
  //    is unchanged; with one, it waits at most GROWTHBOOK_INIT_TIMEOUT_MS and
  //    then falls back to control.
  // Each inline container mounts exactly once, whether it was in the initial
  // DOM or injected later (the manage-appointment page adds its reschedule
  // mount only after a lookup). Gated on the GrowthBook init so a late mount
  // still gets a settled flavour before first paint.
  const mountedInline = new WeakSet<Element>();
  const growthbookReady = initGrowthBook();
  const mountInlineWidgets = (): void => {
    for (const container of Array.from(document.querySelectorAll(INLINE_SELECTOR))) {
      if (mountedInline.has(container)) continue;
      mountedInline.add(container);
      mountInlineWidget(container);
    }
  };
  void growthbookReady.then(mountInlineWidgets);

  // Re-wire when host pages add triggers or inline containers dynamically
  // (single-page apps, late-rendered hero sections, the manage-appointment
  // reschedule mount). Debounced via microtask to coalesce bursts of
  // mutations.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      wireTriggers();
      void growthbookReady.then(mountInlineWidgets);
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
